import { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  Map,
  Radio,
  FileText,
  Settings,
  Wifi,
  LogOut,
  AlertTriangle,
  MapPin,
} from 'lucide-react'
import Dashboard from './pages/Dashboard'
import MapView from './pages/Map'
import Radios from './pages/Radios'
import CustomersDown from './pages/CustomersDown'
import AddressAudit from './pages/AddressAudit'
import Logs from './pages/Logs'
import SettingsPage from './pages/Settings'
import Login from './pages/Login'
import AcceptInvite from './pages/AcceptInvite'
import { initAuth, setAuthToken, getCurrentUser } from './services/api'
import { RoleProvider } from './lib/RoleContext'
import { hasRole } from './lib/role'

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/map', icon: Map, label: 'Map' },
  { to: '/radios', icon: Radio, label: 'Radios' },
  { to: '/customers-down', icon: AlertTriangle, label: 'Customers Down' },
  { to: '/address-audit', icon: MapPin, label: 'Address Audit' },
  { to: '/logs', icon: FileText, label: 'Logs' },
  { to: '/settings', icon: Settings, label: 'Settings' },
]

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [username, setUsername] = useState('')
  const [role, setRole] = useState<string>('viewer')

  useEffect(() => {
    // Check if user is already authenticated
    initAuth()
    const token = localStorage.getItem('auth_token')
    if (token) {
      // Verify token is still valid
      getCurrentUser()
        .then((response) => {
          setUsername(response.data.username)
          setRole(response.data.role)
          setIsAuthenticated(true)
        })
        .catch(() => {
          // Token is invalid, clear it
          setAuthToken(null)
          setIsAuthenticated(false)
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else {
      setIsLoading(false)
    }
  }, [])

  const handleLoginSuccess = () => {
    getCurrentUser()
      .then((response) => {
        setUsername(response.data.username)
        setRole(response.data.role)
        setIsAuthenticated(true)
      })
      .catch(() => {
        setIsAuthenticated(false)
      })
  }

  const handleLogout = () => {
    setAuthToken(null)
    setIsAuthenticated(false)
    setUsername('')
    setRole('viewer')
  }

  if (window.location.pathname === '/accept') {
    return <AcceptInvite />
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />
  }

  return (
    <RoleProvider value={role}>
    <BrowserRouter>
      <div className="min-h-screen bg-gray-100">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-3">
                <Wifi className="h-8 w-8 text-primary-600" />
                <span className="text-xl font-bold text-gray-900">
                  Tarana Control
                </span>
              </div>
              <div className="flex items-center space-x-4">
                <nav className="flex space-x-1">
                  {navItems.filter(item => item.to !== '/settings' || hasRole(role, 'admin')).map(({ to, icon: Icon, label }) => (
                    <NavLink
                      key={to}
                      to={to}
                      className={({ isActive }) =>
                        `flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-primary-100 text-primary-700'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }`
                      }
                    >
                      <Icon className="h-4 w-4 mr-2" />
                      {label}
                    </NavLink>
                  ))}
                </nav>
                <div className="flex items-center space-x-2 ml-4 pl-4 border-l border-gray-200">
                  <span className="text-sm text-gray-600">{username}</span>
                  <button
                    onClick={handleLogout}
                    className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    title="Sign out"
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/map" element={<MapView />} />
            <Route path="/radios" element={<Radios />} />
            <Route path="/customers-down" element={<CustomersDown />} />
            <Route path="/address-audit" element={<AddressAudit />} />
            <Route path="/logs" element={<Logs />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
    </RoleProvider>
  )
}

export default App
